import { installSourceContextReady } from '@/lib/analytics/install-source-context';
import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { claimInstallMarker } from '@/tauri/install-tracking';
import { isTauri } from '@/tauri/utils';
import {
  getDesktopBuildId,
  getDesktopVersion,
  initDesktopVersion,
} from '@/utils/appVersion';
import { logger } from '@/utils/logger/logger';
import { useEffect, useRef } from 'react';

/**
 * Upper bound on the wait for the install-source entities. They resolve behind
 * a release flag and an async MDM probe, and on a flag-off launch nothing
 * resolves them at all, so the wait has to end on its own.
 */
const INSTALL_SOURCE_CONTEXT_TIMEOUT_MS = 5000;

/**
 * Fires `application_install` once on a desktop fresh install. Resolve version
 * before claiming: `version` is required by the schema, so if it's unresolved
 * (only in local `tauri:dev`) we bail without claiming and retry next launch,
 * rather than burning the marker on an event that would fail validation.
 *
 * The install-source wait sits *after* the claim on purpose: only a first
 * launch pays it, at the cost that a quit inside the window loses that
 * device's event.
 */
export function useTrackApplicationInstall(): void {
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (!isTauri()) return;
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const run = async () => {
      try {
        await initDesktopVersion();
        const version = getDesktopVersion();
        if (!version) return;

        const isFirstLaunch = await claimInstallMarker();
        if (!isFirstLaunch) return;

        // install_context / device_mdm reach this event as Snowplow *global*
        // contexts: the latch resolves in the same synchronous block that
        // attaches them, so awaiting it orders the attach before track().
        let timeout: ReturnType<typeof setTimeout> | undefined;
        await Promise.race([
          installSourceContextReady(),
          new Promise<void>((resolve) => {
            timeout = setTimeout(resolve, INSTALL_SOURCE_CONTEXT_TIMEOUT_MS);
          }),
        ]);
        clearTimeout(timeout);

        const build = getDesktopBuildId();
        track({
          event_name: TrackingEventName.ApplicationInstalled,
          props: { version, platform: 'desktop', ...(build ? { build } : {}) },
          options: { amplitude: false },
        });
      } catch (error) {
        logger.error('[applicationInstall] tracking failed', (error as Error)?.message);
      }
    };

    void run();
  }, []);
}

export function ApplicationInstallSync() {
  useTrackApplicationInstall();
  return null;
}
